import { MbeClose } from '@/components/icons';
import {
  Button,
  Flex,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';
import type { SubmitHandler } from 'react-hook-form';
import type { ISetUserAddress, IBaseForm } from '@/types';
import { useTranslation } from 'react-i18next';
import { AddressFields, CtaLoader } from '@/components/shared';
import { hasFormAnyError } from '@/utils';

interface ISettingUserAddressModalProps extends IBaseForm<ISetUserAddress> {
  city: string | undefined;
  countryCode: string | undefined;
  isLoading: boolean;
  province: string | undefined;
  streetAddress: string | undefined;
  zipCode: string | undefined;
}

export const SettingUserAddressModal: React.FC<ISettingUserAddressModalProps> = ({
  city,
  countryCode,
  isLoading,
  onSubmit: providedOnSubmit,
  province,
  streetAddress,
  zipCode,
}): JSX.Element => {
  const { t } = useTranslation();

  const methods = useForm<ISetUserAddress>({
    mode: 'all',
    criteriaMode: 'all',
    defaultValues: { city, countryCode, province, streetAddress, zipCode },
  });

  const {
    formState: { errors },
    handleSubmit: submitHandler,
    // watch,
  } = methods;

  const isDisabled = hasFormAnyError(errors);

  const submitNewPhoneNumberHandler: SubmitHandler<ISetUserAddress> = (newAddress, event): void => {
    event?.preventDefault();
    providedOnSubmit?.(newAddress);
  };

  const modalContainer: HTMLDivElement | null = document.querySelector(
    '.chakra-modal__content-container'
  );

  if (modalContainer) {
    modalContainer.style.zIndex = '999';
  }

  const ctaButtonContent = isLoading ? <CtaLoader /> : t('ACCOUNT.SETTINGS.CONFIRM');

  return (
    <>
      <ModalOverlay backdropFilter="blur(10px) hue-rotate(90deg)" bg="grey" zIndex="998" />
      <ModalContent borderRadius={0} h="80%" minW={{ base: '50%' }} my={{ base: 0, md: '3.75rem' }}>
        <ModalBody
          overflow="hidden"
          p={0}
          paddingInlineEnd={0}
          paddingInlineStart={0}
          position="relative"
        >
          <ModalCloseButton
            _hover={{ bgColor: 'white' }}
            borderRadius="full"
            h={10}
            justifyContent={{ sm: 'flex-end' }}
            position="absolute"
            right="5%"
            top="15px"
            w={10}
            zIndex={3}
          >
            <MbeClose h="3rem" w="3rem" />
          </ModalCloseButton>
          <Flex
            alignItems="center"
            direction="column"
            h="full"
            justifyContent="center"
            position={{ base: 'absolute' }}
            px={{ base: '5%', md: '0' }}
            w={{ base: 'full' }}
            zIndex={2}
          >
            <ModalHeader fontSize={{ base: '24px', md: '32px' }} fontWeight="normal" mb={8}>
              {t('ACCOUNT.SETTINGS.MODIFY_ADDRESS.TITLE')}
            </ModalHeader>
            <FormProvider {...methods}>
              <form
                autoComplete="off"
                id="userAddressForm"
                name="userAddressForm"
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onSubmit={submitHandler(submitNewPhoneNumberHandler)}
              >
                <Flex alignItems="center" direction="column" gap={8} justifyContent="space-between">
                  <AddressFields />

                  <Button disabled={isDisabled} type="submit" variant="mbePrimary" w="100%">
                    {ctaButtonContent}
                  </Button>
                </Flex>
              </form>
            </FormProvider>
          </Flex>
        </ModalBody>
      </ModalContent>
    </>
  );
};
